// MST
import { types, flow, cast } from 'mobx-state-tree'

import {
  IHomeNewsGetAllAttrs,
  IHomeNewsGetAttrs,
  IHomeNewsSaveAttrs,
} from 'interfaces/back-office/content-management/home/news/home-news-view.interface'
import {
  IHomeNewsApiGetAllResponse,
  IHomeNewsApiGetResponse,
  IHomeNewsApiPaginatedResponse,
  IHomeNewsApiSaveResponse,
} from 'interfaces/back-office/content-management/home/news/home-news-api.interface'
import { IHomeNewsDetail } from 'interfaces/back-office/content-management/home/news'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'
import { withHelper_baseResponsePaginationTypes } from 'models/extensions/with-helper'

// Lodash
import omit from 'lodash/omit'

// Utils
import { dateUtils_formatDate } from 'utils/common/date.utils'
import { commonUtils_renderTableNumber } from 'utils/common/common.utils'

// Home News Props
const homeNewsProps = {
  number: types.maybeNull(types.number),
  id: types.maybeNull(types.number),
  title: types.maybeNull(types.string),
  description: types.maybeNull(types.string),
  imageURL: types.maybeNull(types.string),
  originalFileName: types.maybeNull(types.string),
  status: types.maybeNull(types.string),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
}

// Home News List Model
const HomeNewsList = types
  .model('HomeNewsList')
  .props(withHelper_baseResponsePaginationTypes(types.model('HomeNewsList').props(homeNewsProps)))

// Home News Detail Model
const HomeNewsDetail = types.model('HomeNewsDetail').props(omit(homeNewsProps, ['number']))

const HomeNewsStoreModel = types
  .model('HomeNews')
  .props({
    homeNews_isLoading: types.optional(types.boolean, false),
    homeNews_isSaveLoading: types.optional(types.boolean, false),
    homeNews_list: types.optional(HomeNewsList, {}),
    homeNews_detail: types.optional(HomeNewsDetail, {}),
  })
  .actions(self => ({
    homeNews_SET_LOADING: (payload: boolean): void => {
      self.homeNews_isLoading = payload
    },
    homeNews_SET_SAVE_LOADING: (payload: boolean): void => {
      self.homeNews_isSaveLoading = payload
    },
    homeNews_SET_HOME_NEWS_LIST: (payload: IHomeNewsApiPaginatedResponse): void => {
      const mapPayload = {
        ...payload,
        list: payload.list.map((list, index) => {
          return {
            ...list,
            number: commonUtils_renderTableNumber(index, payload),
            createdAt: dateUtils_formatDate(list.createdAt!, { tableFormat: true }),
            updatedAt: dateUtils_formatDate(list.updatedAt!, { tableFormat: true }),
          }
        }),
      }

      self.homeNews_list = cast(mapPayload)
    },
    homeNews_SET_HOME_NEWS: (payload: IHomeNewsDetail): void => {
      self.homeNews_detail = cast(payload)
    },
    homeNews_CLEAR_HOME_NEWS_LIST: (): void => {
      self.homeNews_list = cast({})
    },
    homeNews_CLEAR_HOME_NEWS: (): void => {
      self.homeNews_detail = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch home news list
     *
     * @param {IHomeNewsGetAllAttrs} payload
     *
     * @return {any} any
     */
    homeNews_fetchHomeNewsList: flow(function* (payload: IHomeNewsGetAllAttrs) {
      self.homeNews_SET_LOADING(true)

      const response: IHomeNewsApiGetAllResponse = yield self.environment.homeNewsApi.homeNews_getHomeNewsList({
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) self.homeNews_SET_HOME_NEWS_LIST(response.res.data)

      self.homeNews_SET_LOADING(false)
    }),

    /**
     * @description Fetch home news
     *
     * @param {IHomeNewsGetAttrs} payload
     *
     * @return {any} any
     */
    homeNews_fetchHomeNewsDetail: flow(function* (payload: IHomeNewsGetAttrs) {
      self.homeNews_SET_LOADING(true)

      const response: IHomeNewsApiGetResponse = yield self.environment.homeNewsApi.homeNews_getHomeNewsDetail({
        params: { id: payload.params.id },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        self.homeNews_SET_HOME_NEWS(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response error
        if (payload?.onError) payload.onError()
      }

      self.homeNews_SET_LOADING(false)
    }),

    /**
     * @description Save home news
     *
     * @param {IHomeNewsSaveAttrs} payload
     *
     * @return {any} any
     */
    homeNews_saveHomeNews: flow(function* (payload: IHomeNewsSaveAttrs) {
      self.homeNews_SET_SAVE_LOADING(true)

      const response: IHomeNewsApiSaveResponse = yield self.environment.homeNewsApi.homeNews_saveHomeNews({
        params: { ...payload.params },
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      self.homeNews_SET_SAVE_LOADING(false)
    }),
  }))

export const createHomeNewsStoreDefaultModel = () => types.optional(HomeNewsStoreModel, {})
